import { cva, type VariantProps } from 'class-variance-authority';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styles from './fileSelect.module.scss';
import { Upload, Error } from '../../../icons';
import { AnimatePresence, motion } from 'framer-motion';
import { slideInOut } from '../../../animations';
import { validateFile } from './services';

const FileSelectClass = cva(styles.fileSelect, {
  variants: {},
  defaultVariants: {}
});

type FileSelectProps = VariantProps<typeof FileSelectClass> & {
  value: File | null;
  onChange: (file: File | null) => void;
  className?: string;
  label?: string;
  acceptedFileTypes: {
    label: string;
    contentType: string;
  }[];
  maxFileSizeInMB?: number;
  showImagePreview?: boolean;
};

export default function FileSelect({
  className,
  label,
  acceptedFileTypes,
  maxFileSizeInMB = 1,
  value,
  onChange,
  showImagePreview = false
}: FileSelectProps) {
  const [error, setError] = useState<string | null>(null);
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0] as File;
      const errors = validateFile(
        file,
        acceptedFileTypes.map((type) => type.contentType),
        maxFileSizeInMB
      );

      if (errors.length > 0) {
        onChange(null);
        setError(errors.join(' '));
      } else {
        onChange(file);
        setError(null);
      }
    },
    [acceptedFileTypes, maxFileSizeInMB, onChange]
  );
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const renderBody = () => {
    if (!value) {
      return (
        <div
          {...getRootProps({
            className: FileSelectClass({ className })
          })}
          key="file-select-body"
        >
          <input
            {...getInputProps({
              className: styles.input,
              accept: acceptedFileTypes
                .map((type) => type.contentType)
                .join(', ')
            })}
          />
          <>
            <div className={styles.icon}>
              <Upload />
            </div>
            <div>
              Drag and drop or{' '}
              <span className={styles.bold}>click to upload</span>
            </div>
            <div>
              {acceptedFileTypes
                .map((type) => type.label)
                .join(', ')
                .replace(/, ([^,]*)$/, ' or $1')}
              &nbsp;(max {maxFileSizeInMB}MB)
            </div>
          </>
        </div>
      );
    }

    if (showImagePreview) {
      return (
        <img
          className={styles.previewImage}
          src={URL.createObjectURL(value)}
          alt="uploaded logo"
          key={value.name}
          {...slideInOut(35)}
        />
      );
    } else {
      return (
        <div key={value.name} className={styles.fileName}>
          {value.name}
        </div>
      );
    }
  };

  return (
    <div className={styles.container}>
      {label && <label className={styles.label}>{label}</label>}
      <AnimatePresence initial={false} mode="popLayout">
        <motion.div
          key={value ? value.name : 'file-select'}
          initial={{
            opacity: 0,
            scale: value ? 0.95 : 1,
            y: value ? 0 : -10,
            transformOrigin: value ? 'top left' : 'center'
          }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0,
            transformOrigin: value ? 'top left' : 'center'
          }}
          exit={{
            opacity: 0,
            transition: { duration: 0.2 },
            scale: value ? 0.95 : 1,
            y: value ? 0 : 10,
            transformOrigin: value ? 'top left' : 'center'
          }}
          transition={{
            type: 'spring',
            damping: 15,
            mass: 0.5
          }}
        >
          {renderBody()}
        </motion.div>
      </AnimatePresence>
      <AnimatePresence mode="popLayout">
        {error && (
          <motion.div key={error} className={styles.error} {...slideInOut(35)}>
            <Error />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>
      {value && (
        <button
          className={styles.removeButton}
          type="button"
          onClick={() => {
            onChange(null);
            setError(null);
          }}
        >
          Remove
        </button>
      )}
    </div>
  );
}
